import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { WawiService } from '../../api/generated/services/WawiService';
import type { WawiSalesOverviewView } from '../../api/generated/models/WawiSalesOverviewView';
import { ApiError } from '../../api/generated/core/ApiError';
import { helper } from '../../utils/helper';
import { formatMoney } from '../../utils/statisticsHelpers';
import { periodValueToWawiSalesOverviewParams } from '../../utils/wawiSalesOverviewHelpers';
import type { PeriodFilterValue } from './PeriodFilterDropdown';
import WidgetUpgradeCTA from '../WidgetUpgradeCTA';

const WAWI_DISPLAY_CURRENCY = 'EUR';

interface WawiSalesOverviewWidgetProps {
  periodValue: PeriodFilterValue;
  refreshKey?: number;
}

type ChartRow = { date: string; revenue: number; ordersCount: number };

/** API uses ErrorView: human-readable text in `error` (see openapi). */
function parseErrorMessage(err: unknown): string {
  if (err instanceof ApiError) {
    const parsed = helper.parseApiErrorBody(err);
    if (parsed && typeof parsed === 'object') {
      const o = parsed as Record<string, unknown>;
      if (typeof o.error === 'string' && o.error.trim()) return o.error.trim();
      if (typeof o.message === 'string' && o.message.trim()) return o.message.trim();
    }
    if (typeof err.body === 'string') {
      return err.message || err.body;
    }
    return err.message || String(err.body ?? '');
  }
  return String(err);
}

export const WawiSalesOverviewWidget: React.FC<WawiSalesOverviewWidgetProps> = ({
  periodValue,
  refreshKey = 0,
}) => {
  const { t, i18n } = useTranslation();
  const [data, setData] = useState<WawiSalesOverviewView | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Safety net: the page already hides this widget when `jtl_wawi_enabled` is
  // off, but if the backend still rejects with 403 `subscription_upgrade_required`
  // we swap the chart for the Upgrade CTA instead of surfacing an error.
  const [upgradeRequired, setUpgradeRequired] = useState(false);

  const fetchData = useCallback(() => {
    setLoading(true);
    setError(null);
    setUpgradeRequired(false);
    const q = periodValueToWawiSalesOverviewParams(periodValue);
    WawiService.getAppWawiSalesOverview(q.startDate, q.endDate)
      .then(setData)
      .catch((err) => {
        setData(null);
        if (helper.isUpgradeRequiredError(err)) {
          setUpgradeRequired(true);
          return;
        }
        setError(parseErrorMessage(err));
      })
      .finally(() => setLoading(false));
  }, [periodValue]);

  useEffect(() => {
    fetchData();
  }, [fetchData, refreshKey]);

  const chartData: ChartRow[] = useMemo(() => {
    const series = data?.series ?? [];
    return series.map((p) => ({
      date: p.date ?? '',
      revenue: p.revenue ?? 0,
      ordersCount: p.ordersCount ?? 0,
    }));
  }, [data]);

  const sameCalendarMonth = useMemo(() => {
    if (chartData.length < 2) return true;
    const a = chartData[0].date;
    const b = chartData[chartData.length - 1].date;
    if (a.length < 7 || b.length < 7) return false;
    return a.slice(0, 7) === b.slice(0, 7);
  }, [chartData]);

  const formatTooltipDate = useCallback(
    (ymd: string) => {
      if (!ymd || ymd.length < 10) return ymd;
      const [y, m, d] = ymd.split('-').map(Number);
      if (!y || !m || !d) return ymd;
      const date = new Date(y, m - 1, d);
      return new Intl.DateTimeFormat(i18n.language, {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      }).format(date);
    },
    [i18n.language],
  );

  const renderGrowth = (pct: number | null | undefined) => {
    if (pct == null) {
      return <span className="stat-metric-change stat-metric-change--na">{t('dashboard_wawi_growth_na')}</span>;
    }
    const isPositive = pct >= 0;
    const arrow = isPositive ? '\u2191' : '\u2193';
    return (
      <span className={`stat-metric-change ${isPositive ? 'positive' : 'negative'}`}>
        {arrow} {Math.abs(pct).toFixed(1)}%
      </span>
    );
  };

  const totalRevenue = data?.totalRevenue ?? 0;
  const totalOrders = data?.totalOrders ?? 0;

  if (upgradeRequired) {
    return <WidgetUpgradeCTA className="stat-card" />;
  }

  return (
    <div className="stat-card wawi-sales-overview">
      <div className="stat-card-header wawi-sales-overview__header">
        <h3 className="stat-card-title">{t('dashboard_wawi_sales_overview_title')}</h3>
      </div>
      <div className="wawi-sales-overview__legend-row">
        <span className="wawi-sales-overview__legend-item">
          <span className="wawi-sales-overview__legend-dot wawi-sales-overview__legend-dot--revenue" />
          {t('dashboard_stats_revenue')}
        </span>
        <span className="wawi-sales-overview__legend-item">
          <span className="wawi-sales-overview__legend-dot wawi-sales-overview__legend-dot--orders" />
          {t('dashboard_stats_orders')}
        </span>
      </div>

      {error && (
        <div className="dashboard-error wawi-sales-overview__error" role="alert">
          {error}
        </div>
      )}

      <div className={`chart-container wawi-sales-overview__chart ${loading ? 'wawi-sales-overview__chart--loading' : ''}`}>
        {loading && (
          <div className="wawi-sales-overview__loading">
            <div className="order-sidebar-spinner" />
          </div>
        )}
        {!loading && !error && chartData.length === 0 && data && (
          <div className="wawi-sales-overview__empty">{t('dashboard_stats_no_data')}</div>
        )}
        {!loading && !error && chartData.length > 0 && (
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={chartData} margin={{ top: 8, right: 12, left: 4, bottom: 4 }}>
              <defs>
                <linearGradient id="wawiRevenueGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#2563EB" stopOpacity={0.35} />
                  <stop offset="100%" stopColor="#2563EB" stopOpacity={0.05} />
                </linearGradient>
                <linearGradient id="wawiOrdersGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#EA580C" stopOpacity={0.35} />
                  <stop offset="100%" stopColor="#EA580C" stopOpacity={0.05} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
              <XAxis
                dataKey="date"
                tick={{ fontSize: 10 }}
                stroke="#6B7280"
                tickFormatter={(v) => {
                  const s = String(v);
                  if (sameCalendarMonth && s.length >= 10) return s.slice(8, 10);
                  return s.length >= 10 ? s.slice(5, 10) : s;
                }}
              />
              <YAxis
                yAxisId="left"
                tick={{ fontSize: 10 }}
                stroke="#2563EB"
                domain={['auto', 'auto']}
              />
              <YAxis
                yAxisId="right"
                orientation="right"
                tick={{ fontSize: 10 }}
                stroke="#EA580C"
                domain={['auto', 'auto']}
              />
              <Tooltip
                formatter={(value: number, name: string) => {
                  if (name === 'revenue') return [formatMoney(Number(value), WAWI_DISPLAY_CURRENCY), t('dashboard_stats_revenue')];
                  if (name === 'ordersCount') return [value, t('dashboard_stats_orders')];
                  return [value, name];
                }}
                labelFormatter={(label) => formatTooltipDate(String(label))}
              />
              <Area
                yAxisId="left"
                type="monotone"
                dataKey="revenue"
                name="revenue"
                stroke="#2563EB"
                strokeWidth={2}
                fill="url(#wawiRevenueGradient)"
                dot={false}
                activeDot={{ r: 4 }}
              />
              <Area
                yAxisId="right"
                type="monotone"
                dataKey="ordersCount"
                name="ordersCount"
                stroke="#EA580C"
                strokeWidth={2}
                fill="url(#wawiOrdersGradient)"
                dot={false}
                activeDot={{ r: 4 }}
              />
            </AreaChart>
          </ResponsiveContainer>
        )}
      </div>

      {!loading && !error && data && (
        <div className="stat-metrics-row wawi-sales-overview__footer">
          <div className="stat-metric">
            <span className="stat-metric-label">{t('dashboard_stats_orders')}</span>
            <span className="stat-metric-value">{totalOrders}</span>
            {renderGrowth(data.ordersGrowthPercent)}
          </div>
          <div className="stat-metric">
            <span className="stat-metric-label">{t('dashboard_stats_revenue')}</span>
            <span className="stat-metric-value">{formatMoney(totalRevenue, WAWI_DISPLAY_CURRENCY)}</span>
            {renderGrowth(data.revenueGrowthPercent)}
          </div>
        </div>
      )}
    </div>
  );
};
